import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { recognitionApi } from '@/api/recognition';

interface Props {
  campaignId: string | null;
  programa?: string | null;
  onNext: () => void;
}

interface ColumnInfo {
  name: string;
  descKey: string;
}

/** Mismo orden que RecognitionBulkCsvFormat.IdentityColumns del backend (y del mock): estas son las
 *  columnas de identidad, siempre al PRINCIPIO del archivo. `Mensaje` no va aquí — es la última
 *  columna del archivo, después de todas las de marca (ver nota bajo `bulk-mark-columns`). */
const FIXED_COLUMNS: ColumnInfo[] = [
  { name: 'EmisorEmail', descKey: 'bulk.colEmisorEmailDesc' },
  { name: 'EmisorNomina', descKey: 'bulk.colEmisorNominaDesc' },
  { name: 'ReceptorEmail', descKey: 'bulk.colReceptorEmailDesc' },
  { name: 'ReceptorNomina', descKey: 'bulk.colReceptorNominaDesc' },
];

/** Paso 1 del wizard: qué es cada columna fija, cómo funcionan las columnas de marca, y descarga de
 *  la plantilla real del backend (localizada al idioma actual). */
export function StepInstructions({ campaignId, programa, onNext }: Readonly<Props>) {
  const { t, i18n } = useTranslation('recognition');
  const [templateError, setTemplateError] = useState(false);

  const downloadTemplate = async () => {
    setTemplateError(false);
    try {
      const csv = await recognitionApi.bulkTemplate(i18n.language, campaignId, programa);
      // BOM explícito: res.text() (client.ts) ya descarta cualquier BOM que mande el backend, y los
      // encabezados de esta plantilla llevan nombres acentuados y "·" — sin BOM, Excel en Windows los
      // abre con la codepage del sistema en vez de UTF-8. Mismo patrón que adminBulkUsers/StepInstructions.tsx.
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'reconocimientos-plantilla.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      if (import.meta.env.DEV) console.error('No se pudo descargar la plantilla de carga masiva:', error);
      setTemplateError(true);
    }
  };

  return (
    <div className="bulk-step bulk-step--instructions">
      <p className="bulk-intro">{t('bulk.instructionsIntro')}</p>
      <p className="bulk-match-note"><i className="fas fa-at" /> {t('bulk.matchByIdentity')}</p>
      <p className="bulk-warning-note"><i className="fas fa-triangle-exclamation" /> {t('bulk.onePersonPerRowWarning')}</p>

      <table className="bulk-columns-table">
        <tbody>
          {FIXED_COLUMNS.map((column) => (
            <tr key={column.name}>
              <td className="bulk-col-name">{column.name}</td>
              <td className="bulk-col-desc">{t(column.descKey)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="bulk-mark-columns">
        <h3>{t('bulk.markColumnsTitle')}</h3>
        <p>{t('bulk.markColumnsIntro')}</p>
        <ul>
          <li>{t('bulk.markFundamentalHint')}</li>
          <li>{t('bulk.markBehaviorHint')}</li>
          <li>{t('bulk.markHabitHint')}</li>
        </ul>
        <p>{t('bulk.markColumnsOutro')} {t('bulk.colMensajeDesc')}</p>
      </div>

      <div className="bulk-actions">
        <button type="button" className="btn btn--secondary" onClick={() => void downloadTemplate()}>
          <i className="fas fa-download" /> {t('bulk.downloadTemplate')}
        </button>
        {templateError && (
          <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {t('bulk.templateError')}</p>
        )}
      </div>

      <div className="bulk-wizard-nav">
        <span />
        <button type="button" className="btn btn--send" onClick={onNext}>
          {t('bulk.next')} <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );
}

export default StepInstructions;
